'use client';

import type { ListProduct } from '@/api/types/public';
import { fetchGoodsList } from '@/api/product';
import type { CompleteTrackingParams } from '@/app/(home-page)/types';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import ProductCard from '@/components/Product/ProductCard';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useStore } from '@/providers/app-store-provider';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { serializeKey } from '@/utils/swr/keys';
import { RightArrowFineOutlined } from '@zeelool/icon';
import { useCallback, useId, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import HomeSectionHeading2026 from '../HomeSectionHeading2026/HomeSectionHeading2026';
import type {
  ProductInspirationConfiguredProduct,
  ProductInspirationShowcase2026Props,
  ProductInspirationShowcase2026ValidExposureParams,
} from './types';

const MIN_PRODUCT_COUNT = 2;
const MAX_PRODUCT_COUNT = 6;
const SKELETON_ITEMS = ['product-skeleton-1', 'product-skeleton-2'];
const PRODUCT_IMAGE_HOVER_RULES = [EProductImageType.PRODUCT];
const AUTOPLAY_DELAY = 3000;
const PRODUCTS_PER_PAGE = 2;
const PRODUCT_SLIDE_CLASS_NAME =
  '!mr-0 !flex !h-full !w-full items-center xl:!mr-[8px] xl:!w-[calc(15.625vw+40px)] 3xl:!w-[340px]';
const PRODUCT_CARD_WRAPPER_CLASS_NAME =
  'mx-auto w-[clamp(208px,calc(12.5vw+80px),240px)] xl:w-full';

const normalizeConfiguredText = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.replace(/<br\s*\/?>/gi, '\n').trim();
};

/** 商品坑位 D 位编码（动态商品位，序号 3 位：product_001）；模块码缺失（表外旧数据）时为空串，不硬造。 */
const buildProductSpmCnt = (spmModule: string, productIndex: number) =>
  spmModule ? `${spmModule}.product_${String(productIndex + 1).padStart(3, '0')}` : '';

const getConfiguredProductId = (item: unknown) => {
  if (!item || typeof item !== 'object' || !('sku' in item)) return null;

  const sku = item.sku;
  if (!sku || typeof sku !== 'object') return null;

  const id = 'id' in sku ? sku.id : undefined;
  const productId = Number(id);
  return Number.isFinite(productId) && productId > 0 ? productId : null;
};

const getProductInfo = (productInfo: unknown): ListProduct | null => {
  if (!productInfo || typeof productInfo !== 'object') return null;

  const id = 'id' in productInfo ? productInfo.id : undefined;
  const shopSku = 'shop_sku' in productInfo ? productInfo.shop_sku : undefined;
  if (!id || typeof shopSku !== 'string' || !shopSku.trim()) return null;

  return productInfo as unknown as ListProduct;
};

const getProductCardKey = (product: ListProduct, productIndex: number, currencyName: string) =>
  `${String(product.id || product.shop_sku || productIndex)}-${currencyName}-${JSON.stringify(product)}`;

const getProductKey = (product: ListProduct, productIndex: number) =>
  String(product.shop_sku || product.id || productIndex);

const getFrameColorIndex = (
  baseProduct: ListProduct | undefined,
  selectedProduct: ListProduct,
): number | '' => {
  const correlation = (baseProduct as (ListProduct & { correlation?: ListProduct[] }) | undefined)
    ?.correlation;

  if (Number(baseProduct?.is_glass) !== 1 || !Array.isArray(correlation) || !correlation.length) {
    return '';
  }

  const selectedProductKey = getProductKey(selectedProduct, 0);
  const colorIndex = correlation.findIndex(
    (colorProduct, index) => getProductKey(colorProduct, index) === selectedProductKey,
  );

  return colorIndex >= 0 ? colorIndex : '';
};

const getTrackingProductFields = (product: ListProduct) => {
  const extendedProduct = product as ListProduct & {
    business_sku?: string | number;
    sku?: string | number;
  };

  return {
    businessSku: String(extendedProduct.business_sku || ''),
    productSku: String(extendedProduct.sku || ''),
  };
};

const buildProductTrackingParams = ({
  product,
  baseProduct,
  productIndex,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
}: {
  product: ListProduct;
  baseProduct: ListProduct | undefined;
  productIndex: number;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
}): CompleteTrackingParams => {
  const { businessSku, productSku } = getTrackingProductFields(product);

  return {
    module_index: moduleIndex,
    module_name: componentId,
    module_id: moduleId,
    module_title: moduleTitle,
    mkt_index: productIndex,
    mkt_index_sub: getFrameColorIndex(baseProduct, product),
    mkt_jump_type: '2',
    mkt_jump_content: String(product.shop_sku || product.id || ''),
    shop_sku: String(product.shop_sku || ''),
    business_sku: businessSku,
    product_sku: productSku,
    product_id: String(product.id || ''),
    product_name: String(product.short_name || ''),
  };
};

const buildProductValidExposureParams = ({
  product,
  baseProduct,
  productIndex,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
}: {
  product: ListProduct;
  baseProduct: ListProduct | undefined;
  productIndex: number;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
}): ProductInspirationShowcase2026ValidExposureParams => ({
  module_index: moduleIndex,
  module_id: moduleId,
  module_title: moduleTitle,
  module_name: componentId,
  module_tab_name: '',
  mkt_index: productIndex,
  mkt_index_sub: getFrameColorIndex(baseProduct, product),
  mkt_jump_type: '2',
  mkt_jump_content: String(product.shop_sku || product.id || ''),
});

const buildOperationValidExposureParams = ({
  operation,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
}: {
  operation: ProductInspirationShowcase2026Props['indexData']['operation'];
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
}): ProductInspirationShowcase2026ValidExposureParams => ({
  module_index: moduleIndex,
  module_id: moduleId,
  module_title: moduleTitle,
  module_name: componentId,
  module_tab_name: '',
  mkt_index: 0,
  mkt_index_sub: '',
  mkt_jump_type: String(operation?.jump_type ?? ''),
  mkt_jump_content: String(operation?.Jump_target ?? ''),
});

function ProductCardSkeleton() {
  return (
    <div className="flex w-full animate-pulse flex-col gap-[12px]" aria-hidden>
      <div className="aspect-[470/588] w-full bg-primary-white" />
      <div className="flex flex-col gap-[8px] px-[8px]">
        <div className="h-[17px] w-3/5 rounded bg-primary-white" />
        <div className="h-[19px] w-2/5 rounded bg-primary-white" />
        <div className="h-[22px] w-1/2 rounded bg-primary-white" />
      </div>
    </div>
  );
}

export default function ProductInspirationShowcase2026({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: ProductInspirationShowcase2026Props) {
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const reactId = useId();
  const navigationId = useMemo(
    () => `product-inspiration-${reactId.replace(/[^a-zA-Z0-9-]/g, '-')}`,
    [reactId],
  );
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const exposedProductIds = useRef(new Set<string>());
  const validExposedProductKeys = useRef(new Set<string>());
  const operationValidExposed = useRef(false);
  const { ref: inViewRef, inView } = useInView({ threshold: 0, triggerOnce: true });

  const bindNavigationElements = useCallback((swiper: SwiperType) => {
    const previousButton = previousButtonRef.current;
    const nextButton = nextButtonRef.current;
    if (!previousButton || !nextButton) return;

    const navigation = swiper.params.navigation;
    if (navigation && typeof navigation !== 'boolean') {
      navigation.prevEl = previousButton;
      navigation.nextEl = nextButton;
    }

    const originalNavigation = swiper.originalParams.navigation;
    if (originalNavigation && typeof originalNavigation !== 'boolean') {
      originalNavigation.prevEl = previousButton;
      originalNavigation.nextEl = nextButton;
    }
  }, []);

  const configuredProducts = useMemo(() => {
    if (!Array.isArray(indexData.product_list)) return [];
    return indexData.product_list
      .map((configuredProduct) => ({
        configuredProduct,
        productId: getConfiguredProductId(configuredProduct),
      }))
      .filter(
        (
          item,
        ): item is {
          configuredProduct: ProductInspirationConfiguredProduct;
          productId: number;
        } => typeof item.productId === 'number',
      )
      .slice(0, MAX_PRODUCT_COUNT);
  }, [indexData.product_list]);
  const configuredProductIds = useMemo(
    () => configuredProducts.map(({ productId }) => productId),
    [configuredProducts],
  );
  const operationImage = normalizeConfiguredText(indexData.operation_image);
  const hasValidConfiguration =
    configuredProductIds.length >= MIN_PRODUCT_COUNT && Boolean(operationImage);
  const currencyName = normalizeConfiguredText(currency?.name);
  const configuredProductIdsKey = configuredProductIds.join(',');
  const fixedSkuRequestKey =
    hasValidConfiguration && inView
      ? serializeKey([
          'productInspirationShowcase2026',
          'fixedSkuProducts',
          {
            productIds: configuredProductIdsKey,
            currency: currencyName,
            isLoggedIn: Boolean(token),
          },
        ])
      : null;
  const {
    data: fixedSkuProductsData,
    error: fixedSkuProductsError,
    isLoading,
    mutate: refreshProducts,
  } = useSWR<unknown[] | null>(
    fixedSkuRequestKey,
    async () => {
      const response = await fetchGoodsList({
        product_ids: configuredProductIds,
        currency: currencyName,
        append_correlation: 1,
      });

      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return null;
      return response.data.list;
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 300000,
      keepPreviousData: true,
    },
  );
  const hasAttemptedFetch = fixedSkuProductsData !== undefined || Boolean(fixedSkuProductsError);

  const products = useMemo(() => {
    if (!Array.isArray(fixedSkuProductsData)) return [];

    const productsById = new Map<number, ListProduct>();
    fixedSkuProductsData.forEach((entry) => {
      const product = getProductInfo(entry);
      const productId = Number(product?.id);
      if (product && Number.isFinite(productId)) productsById.set(productId, product);
    });

    return configuredProductIds
      .map((productId) => productsById.get(productId))
      .filter((product): product is ListProduct => Boolean(product));
  }, [configuredProductIds, fixedSkuProductsData]);
  const carouselProducts = useMemo(() => {
    const indexedProducts = products.map((product, productIndex) => ({ product, productIndex }));
    const needsLoopCopies =
      products.length < PRODUCTS_PER_PAGE * 2 || products.length % PRODUCTS_PER_PAGE !== 0;

    return needsLoopCopies ? [...indexedProducts, ...indexedProducts] : indexedProducts;
  }, [products]);

  const headingTitle = normalizeConfiguredText(indexData.layout_title);
  const headingSubtitle = normalizeConfiguredText(indexData.layout_subtitle);
  const shouldShowHeading =
    Number(indexData.layout_title_status) === 1 && Boolean(headingTitle || headingSubtitle);
  const headingTitleColor = normalizeConfiguredText(indexData.layout_title_color);
  const componentId = indexData.component_id;
  const moduleId = String(indexData.blocks_advances_id || indexData.id || '');
  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_pc');
  // 运营图坑位 D 位编码（open_campaign）：点击与有效曝光共用；模块码缺失时为空串，不硬造。
  const openCampaignSpmCnt = spmModule ? `${spmModule}.open_campaign` : '';
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报编码同值，表外静默不打标。
  const moduleDomSpm = useSpmDomAttr(spmModule);
  const openCampaignDomSpm = useSpmDomAttr(openCampaignSpmCnt);
  const operationValidExposureParams = useMemo(
    () =>
      buildOperationValidExposureParams({
        operation: indexData.operation,
        moduleIndex,
        componentId,
        moduleId,
        moduleTitle: headingTitle,
      }),
    [componentId, headingTitle, indexData.operation, moduleId, moduleIndex],
  );

  const buildTrackingParams = useCallback(
    (product: ListProduct, productIndex: number) =>
      buildProductTrackingParams({
        product,
        baseProduct: products[productIndex],
        productIndex,
        moduleIndex,
        componentId,
        moduleId,
        moduleTitle: headingTitle,
      }),
    [componentId, headingTitle, moduleId, moduleIndex, products],
  );

  const handleProductClick = useCallback(
    (productIndex: number, product: ListProduct) => {
      if (!onTrack) return;

      try {
        // 商品点击上报坑位级 spm_cnt（动态商品位，序号 3 位：product_001）。
        onTrack(
          withSpmCnt(
            buildProductValidExposureParams({
              product,
              baseProduct: products[productIndex],
              productIndex,
              moduleIndex,
              componentId,
              moduleId,
              moduleTitle: headingTitle,
            }),
            buildProductSpmCnt(spmModule, productIndex),
          ),
        );
      } catch (error) {
        logger.error('2026 PC product inspiration click tracking error:', error);
      }
    },
    [componentId, headingTitle, moduleId, moduleIndex, onTrack, products, spmModule],
  );

  const handleProductValidExposure = useCallback(
    (productIndex: number, product: ListProduct) => {
      if (!onExpTrack && !onValidExpTrack) return;

      const productKey = `${getProductKey(product, productIndex)}-${moduleIndex}`;
      const trackingParams = buildTrackingParams(product, productIndex);
      const baseProduct = products[productIndex];
      const colorIndex = getFrameColorIndex(baseProduct, product);
      const validExposureKey = `${productIndex}-${productKey}-${colorIndex}`;

      try {
        if (onExpTrack && !exposedProductIds.current.has(productKey)) {
          onExpTrack(trackingParams);
          exposedProductIds.current.add(productKey);
        }
        if (onValidExpTrack && !validExposedProductKeys.current.has(validExposureKey)) {
          onValidExpTrack(
            // 商品有效曝光为 item 级，spm_cnt 追加坑位 D 位（动态商品位，序号 3 位：product_001）。
            withSpmCnt(
              buildProductValidExposureParams({
                product,
                baseProduct,
                productIndex,
                moduleIndex,
                componentId,
                moduleId,
                moduleTitle: headingTitle,
              }),
              buildProductSpmCnt(spmModule, productIndex),
            ),
          );
          validExposedProductKeys.current.add(validExposureKey);
        }
      } catch (error) {
        logger.error('2026 PC product inspiration exposure tracking error:', error);
      }
    },
    [
      buildTrackingParams,
      componentId,
      headingTitle,
      moduleId,
      moduleIndex,
      onExpTrack,
      onValidExpTrack,
      products,
      spmModule,
    ],
  );

  const handleOperationValidExposure = useCallback(() => {
    if (!onValidExpTrack || operationValidExposed.current) return;

    try {
      // 运营图有效曝光为 item 级，spm_cnt 追加坑位 D 位（open_campaign，与点击同坑位）。
      onValidExpTrack(withSpmCnt(operationValidExposureParams, openCampaignSpmCnt));
      operationValidExposed.current = true;
    } catch (error) {
      logger.error('2026 PC product inspiration operation valid exposure tracking error:', error);
    }
  }, [onValidExpTrack, openCampaignSpmCnt, operationValidExposureParams]);

  const { ref: operationValidExposureRef } = useValidExposure({
    ts: `product-inspiration-operation-${moduleId}-${operationImage}`,
    callback: handleOperationValidExposure,
  });

  const handleOperationClick = () => {
    if (!onTrack) return;

    try {
      // 运营图点击即打开专题活动，上报坑位级 spm_cnt；浅拷贝避免污染曝光共用对象。
      onTrack(withSpmCnt(operationValidExposureParams, openCampaignSpmCnt));
    } catch (error) {
      logger.error('2026 PC product inspiration operation click tracking error:', error);
    }
  };

  if (!hasValidConfiguration) return null;
  if (hasAttemptedFetch && products.length < MIN_PRODUCT_COUNT) return null;

  const hasProducts = !isLoading && products.length >= MIN_PRODUCT_COUNT;

  return (
    <section
      ref={inViewRef}
      data-component-id={componentId}
      className="w-full bg-primary-white"
      {...moduleDomSpm}
    >
      {shouldShowHeading ? (
        <HomeSectionHeading2026
          title={headingTitle}
          subtitle={headingSubtitle}
          titleColor={headingTitleColor || undefined}
          subtitleColor={
            indexData.layout_subtitle_color || indexData.layout_subtitl_color || undefined
          }
          titleTag="h2"
          subtitleTag="h5"
        />
      ) : null}

      <div className="grid w-full grid-cols-2 bg-[#f7f7f7]">
        <div className="relative flex min-w-0 items-center justify-center overflow-hidden">
          <button
            ref={previousButtonRef}
            type="button"
            aria-label="Previous products"
            className={`${navigationId}-prev absolute left-[clamp(104px,calc(18.75vw-88px),152px)] top-1/2 z-10 flex size-[32px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 text-primary-white xl:left-[clamp(28px,calc(3.125vw-12px),48px)] [&.swiper-button-disabled]:hidden [&.swiper-button-lock]:hidden`}
          >
            <span className="flex size-[20px] rotate-180 items-center justify-center">
              <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
            </span>
          </button>
          <button
            ref={nextButtonRef}
            type="button"
            aria-label="Next products"
            className={`${navigationId}-next absolute right-[clamp(104px,calc(18.75vw-88px),152px)] top-1/2 z-10 flex size-[32px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 text-primary-white xl:right-[clamp(28px,calc(3.125vw-12px),48px)] [&.swiper-button-disabled]:hidden [&.swiper-button-lock]:hidden`}
          >
            <span className="flex size-[20px] items-center justify-center">
              <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
            </span>
          </button>

          <div className="h-full w-full xl:w-[calc(31.25vw+88px)] 3xl:w-[688px]">
            {/* 商品卡收藏坑位码由 spmModule 动态拼接，无需 SPM 作用域查表。 */}
            <Swiper
              key={hasProducts ? 'products' : 'skeleton'}
              modules={[Autoplay, Navigation]}
              onBeforeInit={bindNavigationElements}
              slidesPerView="auto"
              slidesPerGroup={1}
              breakpoints={{
                // 与卡片宽度的 xl 断点保持一致，单卡布局每次只切换一个商品。
                1280: { slidesPerGroup: PRODUCTS_PER_PAGE },
              }}
              spaceBetween={0}
              loop={hasProducts}
              loopAdditionalSlides={0}
              watchOverflow
              autoplay={
                hasProducts
                  ? {
                      delay: AUTOPLAY_DELAY,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }
                  : false
              }
              observer
              observeParents
              navigation={{
                prevEl: `.${navigationId}-prev`,
                nextEl: `.${navigationId}-next`,
              }}
              className="!h-full !overflow-hidden"
            >
              {hasProducts
                ? carouselProducts.map(({ product, productIndex }, carouselIndex) => (
                    <SwiperSlide
                      key={`${getProductCardKey(product, productIndex, currencyName)}-${carouselIndex}`}
                      className={PRODUCT_SLIDE_CLASS_NAME}
                    >
                      <div className={PRODUCT_CARD_WRAPPER_CLASS_NAME}>
                        <ProductCard
                          product={product}
                          index={productIndex}
                          spmModule={spmModule}
                          productSpmCnt={buildProductSpmCnt(spmModule, productIndex)}
                          wished={() => void refreshProducts()}
                          removed={() => void refreshProducts()}
                          showCorrelation
                          showReviewStar
                          showTryOn={false}
                          showMember={false}
                          showTag={false}
                          showOriginalPrice
                          showName={false}
                          namePriceInline={false}
                          imageClassName="!aspect-[470/588] !rounded-none"
                          productInfoClassName="!px-[8px] !pt-[8px] [&_.row-start-2]:min-h-[24px]"
                          imageHoverRule={PRODUCT_IMAGE_HOVER_RULES}
                          productImagePriority="white-first"
                          hoverProductImagePriority="white-second-gray-first"
                          productImageWhiteBg
                          handelClick={handleProductClick}
                          onValidExposure={handleProductValidExposure}
                        />
                      </div>
                    </SwiperSlide>
                  ))
                : SKELETON_ITEMS.map((item) => (
                    <SwiperSlide key={item} className={PRODUCT_SLIDE_CLASS_NAME}>
                      <div className={PRODUCT_CARD_WRAPPER_CLASS_NAME}>
                        <ProductCardSkeleton />
                      </div>
                    </SwiperSlide>
                  ))}
            </Swiper>
          </div>
        </div>

        <div ref={operationValidExposureRef} className="aspect-[5/4] w-full overflow-hidden">
          <MagicJump
            item={indexData.operation}
            prefetch
            onBeforeJump={handleOperationClick}
            aria-label={headingTitle || headingSubtitle || undefined}
            className="relative block size-full"
            {...openCampaignDomSpm}
          >
            <Image
              src={operationImage}
              alt={headingTitle}
              fill
              priority={moduleIndex < 3}
              loading={moduleIndex < 3 ? 'eager' : 'lazy'}
              sizes="50vw"
              className="object-cover"
            />
          </MagicJump>
        </div>
      </div>
    </section>
  );
}
